import { SITE } from "@/lib/site";

type Socials = {
  instagram?: string | null; facebook?: string | null; linkedin?: string | null;
  youtube?: string | null; website?: string | null;
};
type NAP = { name?: string | null; address?: string | null; phone?: string | null; hours?: string | null };
type Partner = {
  name?: string | null; description?: string | null; logo?: string | null;
  website?: string | null; whatsapp?: string | null;
};

function isSafeGoogleMapsEmbed(raw: string): boolean {
  if (!raw) return false;
  const iframe = /<iframe\b[^>]*\bsrc=["'](https:\/\/www\.google\.com\/maps\/embed[^"']*)["'][^>]*><\/iframe>/i;
  return iframe.test(raw);
}

function sanitizeGoogleMapsEmbed(raw: string): string | null {
  const m = raw.match(/<iframe\b[^>]*\bsrc=["'](https:\/\/www\.google\.com\/maps\/embed[^"']*)["'][^>]*><\/iframe>/i);
  if (!m) return null;
  const src = m[1];
  // Rebuild the iframe with only the whitelisted attributes we care about.
  return `<iframe src="${src}" width="100%" height="360" style="border:0" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Localização no Google Maps"></iframe>`;
}

function normalizePhoneForTel(phone: string): string {
  return phone.replace(/[^\d+]/g, "");
}
function normalizeWhatsapp(raw: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  // Accept full https://wa.me/... too
  const m = trimmed.match(/(?:wa\.me\/|whatsapp\.com\/send\?phone=)?(\+?\d[\d\s\-()]*)/i);
  if (!m) return null;
  const digits = m[1].replace(/\D/g, "");
  if (!digits) return null;
  // If already includes country code (12+ digits), keep as-is; otherwise assume BR (+55).
  const full = digits.length >= 12 ? digits : `55${digits}`;
  return full;
}

function SocialLink({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-foreground hover:bg-primary/10 hover:border-primary/40">
      {label}
    </a>
  );
}

export function BusinessProfileCard({
  nap, socials, googleMapsEmbed,
}: { nap?: NAP | null; socials?: Socials | null; googleMapsEmbed?: string | null }) {
  const s = socials ?? {};
  const safeEmbed = googleMapsEmbed && isSafeGoogleMapsEmbed(googleMapsEmbed)
    ? sanitizeGoogleMapsEmbed(googleMapsEmbed)
    : null;
  const hasAny = Boolean(nap?.name || nap?.address || nap?.phone || nap?.hours || safeEmbed
    || s.website || s.instagram || s.facebook || s.linkedin || s.youtube);
  if (!hasAny) return null;
  return (
    <aside className="not-prose my-8 overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-background to-background p-6 md:p-8">
      <div className="grid gap-6 md:grid-cols-[1fr_1.1fr]">
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Perfil da empresa</span>
          {nap?.name && <h2 className="mt-2 text-2xl font-bold leading-tight text-foreground md:text-3xl">{nap.name}</h2>}
          <dl className="mt-4 space-y-2 text-sm">
            {nap?.address && (
              <div className="flex gap-2"><dt className="font-semibold text-foreground shrink-0">Endereço:</dt><dd className="text-muted-foreground">{nap.address}</dd></div>
            )}
            {nap?.phone && (
              <div className="flex gap-2"><dt className="font-semibold text-foreground shrink-0">Telefone:</dt>
                <dd><a href={`tel:${normalizePhoneForTel(nap.phone)}`} className="text-primary hover:underline">{nap.phone}</a></dd>
              </div>
            )}
            {nap?.hours && (
              <div className="flex gap-2"><dt className="font-semibold text-foreground shrink-0">Horário:</dt><dd className="text-muted-foreground">{nap.hours}</dd></div>
            )}
          </dl>
          {(s.website || s.instagram || s.facebook || s.linkedin || s.youtube) && (
            <div className="mt-5 flex flex-wrap gap-2">
              {s.website && <SocialLink href={s.website} label="Site oficial" />}
              {s.instagram && <SocialLink href={s.instagram} label="Instagram" />}
              {s.facebook && <SocialLink href={s.facebook} label="Facebook" />}
              {s.linkedin && <SocialLink href={s.linkedin} label="LinkedIn" />}
              {s.youtube && <SocialLink href={s.youtube} label="YouTube" />}
            </div>
          )}
        </div>
        {safeEmbed && (
          <div className="overflow-hidden rounded-2xl border border-border bg-secondary/40">
            <div className="aspect-video w-full" dangerouslySetInnerHTML={{ __html: safeEmbed }} />
          </div>
        )}
      </div>
    </aside>
  );
}

export function PartnersSection({ partners }: { partners?: Partner[] | null }) {
  const list = (partners ?? []).filter((p): p is Partner => Boolean(p?.name));
  if (list.length === 0) return null;
  const message = `Olá! Vim pelo blog ${SITE.name} (${SITE.baseUrl.replace(/^https?:\/\//, "")}) e gostaria de saber mais sobre os serviços de vocês.`;
  return (
    <section className="not-prose my-10">
      <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">Empresas parceiras recomendadas</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Fale com os parceiros mencionando que veio pelo {SITE.name} — o WhatsApp já vai pré-preenchido com essa mensagem.
      </p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {list.map((p, i) => {
          const waDigits = p.whatsapp ? normalizeWhatsapp(p.whatsapp) : null;
          const waHref = waDigits ? `https://wa.me/${waDigits}?text=${encodeURIComponent(message)}` : null;
          return (
            <article key={`${p.name}-${i}`} className="flex flex-col gap-4 rounded-2xl border border-border bg-background/60 p-5 shadow-sm">
              <div className="flex items-start gap-4">
                {p.logo ? (
                  <img src={p.logo} alt={p.name ?? "Logo do parceiro"} loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-xl border border-border bg-white object-contain p-1" />
                ) : (
                  <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-secondary text-lg font-bold text-muted-foreground">
                    {(p.name ?? "?").slice(0, 1)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                  {p.description && <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.website && (
                  <a href={p.website} target="_blank" rel="noopener nofollow"
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-4 py-2 text-xs font-semibold text-foreground hover:bg-primary/10 hover:border-primary/40">
                    Visitar site
                  </a>
                )}
                {waHref && (
                  <a href={waHref} target="_blank" rel="noopener nofollow"
                    className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white hover:brightness-110">
                    Chamar no WhatsApp
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}